import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Controller, useFormContext, useFormState, useWatch } from 'react-hook-form';
import { RotateCw, Trash2 } from 'lucide-react';
import { DashboardPageFormField } from '@shigo-ui/dashboard-ui';
import {
  AutocompleteMultiSelect,
  type AutocompleteMultiSelectOption,
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shigo-ui/ui';

import { dashboardT } from '../../../../lib/messages';
import { LiverampKeyOperator } from '../../advancedTargetingModel';
import type { LiverampSegment } from '../../builder/api';
import type { TargetingBuilderFormValue } from '../../builder/model';
import type { LiverampSegmentClient } from './LiverampTargetingForm.types';

const liverampSearchMinimumLength = 1;

export function LiverampRule({
  client,
  groupIndex,
  ruleIndex,
  onRemove,
}: {
  client: LiverampSegmentClient;
  groupIndex: number;
  ruleIndex: number;
  onRemove: () => void;
}) {
  'use no memo';

  const { control, getFieldState } = useFormContext<TargetingBuilderFormValue>();
  const formState = useFormState({ control });
  const [searchTerm, setSearchTerm] = useState('');
  const prefix =
    `liverampTargeting.groups.${String(groupIndex)}.keys.${String(ruleIndex)}` as `liverampTargeting.groups.${number}.keys.${number}`;
  const selectedIds = useWatch({ control, name: `${prefix}.values` });
  const [initialSelectedIds] = useState(() => [...selectedIds]);
  const [selectedOptions, setSelectedOptions] = useState<AutocompleteMultiSelectOption[]>([]);
  const searchQuery = useQuery({
    queryKey: [...(client.queryScope ?? []), 'liveramp-segments', 'search', searchTerm],
    enabled: searchTerm.trim().length >= liverampSearchMinimumLength,
    queryFn: () => client.listLiverampSegments({ searchTerm }),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  const initialSegmentsQuery = useQuery({
    queryKey: [...(client.queryScope ?? []), 'liveramp-segments', 'initial', initialSelectedIds],
    enabled: initialSelectedIds.length > 0,
    queryFn: () => client.listLiverampSegments({ segmentIds: initialSelectedIds }),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  const options = useMemo(() => {
    const fetchedOptions = [
      ...(initialSegmentsQuery.data?.content ?? []),
      ...(searchQuery.data?.content ?? []),
    ].map(toLiverampOption);
    return Array.from(
      new Map(
        [...selectedOptions, ...fetchedOptions].map((option) => [option.id, option]),
      ).values(),
    );
  }, [initialSegmentsQuery.data, searchQuery.data, selectedOptions]);
  const onSearch = useCallback((term: string) => {
    setSearchTerm(term);
  }, []);
  const error = searchQuery.isError || initialSegmentsQuery.isError;
  const validationError = getFieldState(`${prefix}.values`, formState).error?.message;
  return (
    <div className="grid gap-3 border-t pt-4 md:grid-cols-[10rem_10rem_1fr_auto]">
      <DashboardPageFormField label={dashboardT('targetingBuilder.segmentAction')}>
        <Controller
          control={control}
          name={`${prefix}.operator`}
          render={({ field }) => (
            <Select value={field.value} onValueChange={field.onChange}>
              <SelectTrigger aria-label={dashboardT('targetingBuilder.segmentAction')}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.values(LiverampKeyOperator).map((value) => (
                  <SelectItem key={value} value={value}>
                    {dashboardT(`targetingBuilder.liverampOperators.${value}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
      </DashboardPageFormField>
      <DashboardPageFormField label={dashboardT('targetingBuilder.vendor')}>
        <Input disabled value="LiveRamp" aria-label={dashboardT('targetingBuilder.vendor')} />
      </DashboardPageFormField>
      <DashboardPageFormField
        label={dashboardT('targetingBuilder.segments')}
        required
        error={error ? dashboardT('targetingBuilder.segmentsError') : validationError}
      >
        <Controller
          control={control}
          name={`${prefix}.values`}
          render={({ field }) => (
            <AutocompleteMultiSelect
              label={dashboardT('targetingBuilder.segments')}
              value={field.value}
              options={options}
              loading={searchQuery.isLoading || initialSegmentsQuery.isLoading}
              placeholder={dashboardT('targetingBuilder.searchSegments')}
              emptyMessage={dashboardT('targetingBuilder.noSegments')}
              minSearchLength={liverampSearchMinimumLength}
              onSearchTermChange={onSearch}
              searchDebounceMs={300}
              onValueChange={(nextIds) => {
                setSelectedOptions((current) => {
                  const byId = new Map(
                    [...current, ...options].map((option) => [option.id, option]),
                  );
                  return nextIds.flatMap((id) => {
                    const option = byId.get(id);
                    return option ? [option] : [];
                  });
                });
                field.onChange(nextIds);
              }}
            />
          )}
        />
        {error ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="mt-1 w-fit"
            onClick={() => {
              if (searchQuery.isError) void searchQuery.refetch();
              if (initialSegmentsQuery.isError) void initialSegmentsQuery.refetch();
            }}
          >
            <RotateCw className="h-4 w-4" aria-hidden="true" />
            {dashboardT('targeting.retry')}
          </Button>
        ) : null}
      </DashboardPageFormField>
      <Button
        type="button"
        size="icon"
        variant="ghost"
        className="md:mt-[25px] md:self-start"
        aria-label={dashboardT('targetingBuilder.removeRule')}
        onClick={onRemove}
      >
        <Trash2 className="h-4 w-4" aria-hidden="true" />
      </Button>
    </div>
  );
}

function toLiverampOption(segment: LiverampSegment): AutocompleteMultiSelectOption {
  return {
    id: segment.segmentId ?? segment.id,
    label: segment.providerName
      ? `${segment.segmentName} (${segment.providerName})`
      : segment.segmentName || segment.id,
  };
}
